import { useRef, useEffect, useCallback } from 'react';

const TOOLTIP_STYLE =
  'position:fixed;pointer-events:none;z-index:1000;background:#333;color:#fff;padding:8px 12px;border-radius:4px;font-size:12px;line-height:1.4;max-width:280px;white-space:normal;overflow-wrap:break-word;';

const VIEWPORT_MARGIN = 8;
const CURSOR_OFFSET_X = 12;
const CURSOR_OFFSET_Y = 40;

type TooltipEvent = { clientX: number; clientY: number } | FocusEvent | React.FocusEvent;

function positionTooltip(el: HTMLDivElement, clientX: number, clientY: number): void {
  const { width, height } = el.getBoundingClientRect();
  const { innerWidth, innerHeight } = window;

  let left = clientX + CURSOR_OFFSET_X;
  if (left + width > innerWidth - VIEWPORT_MARGIN) {
    left = clientX - CURSOR_OFFSET_X - width;
  }
  left = Math.max(VIEWPORT_MARGIN, Math.min(left, innerWidth - width - VIEWPORT_MARGIN));

  let top = clientY - CURSOR_OFFSET_Y;
  if (top < VIEWPORT_MARGIN) {
    top = clientY + CURSOR_OFFSET_Y / 2;
  }
  top = Math.max(VIEWPORT_MARGIN, Math.min(top, innerHeight - height - VIEWPORT_MARGIN));

  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
}

export function useChartTooltip() {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (ref.current) {
        ref.current.remove();
        ref.current = null;
      }
    };
  }, []);

  const getOrCreate = useCallback((): HTMLDivElement => {
    if (!ref.current) {
      ref.current = document.createElement('div');
      ref.current.style.cssText = TOOLTIP_STYLE;
      document.body.appendChild(ref.current);
    }
    return ref.current;
  }, []);

  const show = useCallback(
    (event: TooltipEvent, html: string) => {
      const el = getOrCreate();
      el.innerHTML = html;
      el.style.display = 'block';
      if ('clientX' in event) {
        positionTooltip(el, event.clientX, event.clientY);
      }
    },
    [getOrCreate],
  );

  const move = useCallback((event: { clientX: number; clientY: number }) => {
    if (ref.current) {
      positionTooltip(ref.current, event.clientX, event.clientY);
    }
  }, []);

  const hide = useCallback(() => {
    if (ref.current) {
      ref.current.style.display = 'none';
    }
  }, []);

  return { show, move, hide };
}
